import React, { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment-timezone";
import { Controller, useFormContext } from "react-hook-form";

import { useNotification } from "@hooks/useNotification";

import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import { Switch } from "@components/tailwindComponents";

const DATE_FORMAT = "YYYY-MM-DD";

export default function PeriodDatePickers(props) {
  const { t: translate } = useTranslation([
    "pages.employés.parcours&Certifications",
    "message"
  ]);
  const { showError } = useNotification();
  const { control, watch, formState, setValue, trigger } = useFormContext();
  const [salaryBegin, salaryEnd, isInProgress] = watch([
    "salaryBegin",
    "salaryEnd",
    "isInProgress"
  ]);
  const { readOnly = false, career } = props;

  const isSalaryDateInvalid = useMemo(
    () => formState.errors.salaryBegin,
    [formState.errors.salaryBegin]
  );

  // -- 🧩 Date Limitation 🧩 --
  const minDateLimit = useMemo(
    () =>
      career?.dateStart ? moment(career.dateStart, DATE_FORMAT).toDate() : null,
    [career]
  );

  const maxDateLimit = useMemo(
    () =>
      career?.endDate ? moment(career.endDate, DATE_FORMAT).toDate() : null,
    [career]
  );

  useEffect(() => {
    if (!career?.status) {
      return;
    }

    setValue("salaryEnd", "", { shouldValidate: true });

    if (!career.dateStart) {
      return;
    }

    setValue("salaryBegin", moment(career.dateStart, DATE_FORMAT).toDate(), {
      shouldValidate: true
    });
  }, [career?.status, career?.dateStart, setValue]);

  useEffect(() => {
    if (!salaryBegin || !salaryEnd) {
      return;
    }

    void trigger(["salaryBegin", "salaryEnd"]);
  }, [salaryBegin, salaryEnd, trigger]);

  function handleDateLimitError() {
    showError("La date choisie est en dehors de la période valide pour ce poste");
  }

  function noOverlapRule(startValue, endValue) {
    if (career?.status || isInProgress) {
      return true;
    }

    if (!startValue || !endValue) {
      return true;
    }

    const startMoment = moment(startValue);
    const endMoment = moment(endValue);

    for (let salary of props.otherSalaries || []) {
      const otherStart = moment(salary.salaryBegin);
      const otherEnd = moment(salary.salaryEnd);

      if (!(otherEnd.isSameOrBefore(startMoment) || otherStart.isSameOrAfter(endMoment))) {
        return false;
      }
    }
    return true;
  }

  // -- 🧩 Form Validation 🧩 --
  const salaryBeginRule = useMemo(() => {
    return {
      required: true,
      validate: {
        noOverlap: (value) => noOverlapRule(value, salaryEnd)
      }
    };
  }, [salaryEnd, props.otherSalaries]);

  const salaryEndRule = useMemo(() => {
    return {
      validate: {
        required: (value) => career?.status || isInProgress || !!value,
        isAfterSalaryBegin: (value) =>
          career?.status ||
          isInProgress ||
          moment(value).isSameOrAfter(salaryBegin),
        noOverlap: (value) => noOverlapRule(salaryBegin, value)
      }
    };
  }, [career?.status, salaryBegin, isInProgress, props.otherSalaries]);

  return (
    <>
      <div className="arh-mb-3">
        <label
          className={`arh-mb-1 arh-block arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${isSalaryDateInvalid
              ? "arh-text-[#F60A20]"
              : readOnly
                ? "arh-text-greyDisabledText"
                : "arh-text-darkColor"
            }`}
        >
          {translate("Période") + " *"}
        </label>
        <div className="filterDate wadeDatePicker arh-flex arh-w-full arh-justify-between arh-space-x-4">
          <div className="arhDatePicker employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-w-1/2">
            <Controller
              control={control}
              name="salaryBegin"
              rules={salaryBeginRule}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <DefaultDatePicker
                  placeholder={translate("Du")}
                  dateValue={value}
                  setDateValue={onChange}
                  invalid={invalid}
                  classPosition="calendar_left"
                  readOnly={readOnly || Boolean(career?.status)}
                  minDate={minDateLimit}
                  maxDate={maxDateLimit}
                  onDateLimitError={handleDateLimitError}
                />
              )}
            />
          </div>

          <div className="employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-w-1/2">
            <Controller
              control={control}
              name="salaryEnd"
              rules={salaryEndRule}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <DefaultDatePicker
                  placeholder={translate("Au")}
                  readOnly={readOnly || isInProgress || Boolean(career?.status)}
                  dateValue={value}
                  setDateValue={onChange}
                  invalid={invalid}
                  classPosition="calendar_right"
                  minDate={minDateLimit}
                  maxDate={maxDateLimit}
                  onDateLimitError={handleDateLimitError}
                />
              )}
            />
          </div>
        </div>
      </div>

      {!props.readOnly && (
        <div className="-arh-mt-1 arh-flex arh-w-full arh-items-center arh-justify-end">
          <Controller
            control={control}
            name="isInProgress"
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <>
                <Switch
                  color="arh-bg-greenColor"
                  checked={value}
                  handleChange={() => onChange(!value)}
                  disabled={Boolean(career?.endDate)}
                />
                <span className="arh-ml-2 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor">
                  {translate("En cours")}
                </span>
              </>
            )}
          />
        </div>
      )}
    </>
  );
}
